import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Menu,
  MenuItem,
  Stack,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import { apiErrorMessage, launchApi } from '../api';
import { help } from '../helpTexts';
import { chipButtonSx, launchColors } from '../launchTheme';
import { Assignee, CalendarWindow, YearbookRow, YearbookSheet, YearbookWeekGroup } from '../types';
import InfoTip from '../ui/InfoTip';
import SectionCard from '../ui/SectionCard';
import { today } from '../helpers';
import { athenaEan, athenaProductName } from './athenaName';
import BulkEditBar, { BulkField } from './BulkEditBar';
import { ContactCard, ContactCardsDialog } from './CategoryContactCell';
import { hiddenColumns, saveHiddenColumns } from './columnPrefs';
import ReminderAlerts from './ReminderAlerts';
import { customerChainOf, CustomerContact } from './customerContacts';
import CustomerInfoDialog from './CustomerInfoDialog';
import { groupRows } from './groupRows';
import { COLUMN_LABELS, ROW_COLUMNS, RowColumn } from './labels';
import { sampleTotals } from './sampleTotals';
import { useYearbookProducts } from './useYearbookProducts';
import YearbookTable from './YearbookTable';

interface Props {
  canEdit: boolean;
  canImport: boolean;
  canOpenProductInfo?: boolean;
  canOpenAlcoholInfo?: boolean;
  currentUserId: string;
  onDueReminders?: (count: number) => void;
  active?: boolean;
  people?: Assignee[];
}

const NO_PEOPLE: Assignee[] = [];

type Filter = 'all' | 'products';

const commandId = () => `yb-${Date.now().toString(36)}-${Math.random().toString(16).slice(2, 8)}`;

const uniqueLabel = (used: Set<string>, base: string) => {
  if (!used.has(base)) return base;
  let index = 2;
  while (used.has(`${base} ${index}`)) index += 1;
  return `${base} ${index}`;
};

const weekFields = (week: YearbookWeekGroup) => ({
  announcementDate: week.announcementDate,
  announcementRaw: week.announcementRaw,
  statedWeek: week.statedWeek,
  revisionWeek: week.revisionWeek,
  revisionYear: week.revisionYear,
});

const WINDOW_FIELDS = new Set(['announcementDate', 'announcementRaw', 'statedWeek', 'revisionWeek', 'revisionYear', 'category', 'articleNumber', 'articleName', 'action', 'assetId']);

const touchesWindow = (rows: YearbookRow[], fields: string[]) => rows.some((row) => row.windowFamilyId) && fields.some((field) => WINDOW_FIELDS.has(field));

const YearbookView: React.FC<Props> = ({ canEdit, canImport, canOpenProductInfo = false, canOpenAlcoholInfo = false, currentUserId, onDueReminders, active = true, people = NO_PEOPLE }) => {
  const [sheets, setSheets] = useState<YearbookSheet[]>([]);
  const [windows, setWindows] = useState<CalendarWindow[]>([]);
  const [sheetId, setSheetId] = useState('');
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<Filter>('all');
  const [hidden, setHidden] = useState<RowColumn[]>([]);
  const [columnMenu, setColumnMenu] = useState<HTMLElement | null>(null);
  const [rowTarget, setRowTarget] = useState<YearbookRow | null>(null);
  const [weekTarget, setWeekTarget] = useState<YearbookWeekGroup | null>(null);
  const [selected, setSelected] = useState<string[]>([]);
  const [pendingDelete, setPendingDelete] = useState<YearbookRow[]>([]);
  const [newChainOpen, setNewChainOpen] = useState(false);
  const [newChainName, setNewChainName] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [contacts, setContacts] = useState<CustomerContact[]>([]);
  const [customerOpen, setCustomerOpen] = useState(false);
  const [openCards, setOpenCards] = useState<{ cards: ContactCard[]; start: number } | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(() => new Set());
  const [status, setStatus] = useState<'loading' | 'ready' | 'failed'>('loading');
  const [needle, setNeedle] = useState('');
  const [productRefresh, setProductRefresh] = useState(0);
  const wasActive = useRef(active);

  const load = () => {
    launchApi.yearbook()
      .then((state) => {
        setSheets(state.sheets);
        setSheetId((current) => current || state.sheets[0]?.id || '');
        setStatus('ready');
      })
      .catch((requestError) => {
        setError(apiErrorMessage(requestError, 'Årskalendern kunde inte hämtas.'));
        setStatus((current) => (current === 'ready' ? current : 'failed'));
      });
    launchApi.calendarWindows().then(setWindows).catch(() => setWindows([]));
    launchApi.customerContacts().then(setContacts).catch(() => setContacts([]));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (active && !wasActive.current) {
      load();
      setProductRefresh((value) => value + 1);
    }
    wasActive.current = active;
  }, [active]);

  useEffect(() => {
    const timer = window.setTimeout(() => setNeedle(query.trim().toLocaleLowerCase('sv')), 200);
    return () => window.clearTimeout(timer);
  }, [query]);

  const sheet = sheets.find((item) => item.id === sheetId) || null;
  const { matches: products, failed: productsFailed } = useYearbookProducts(sheet, productRefresh);
  const syncedNames = useRef('');
  const asOf = today();
  const totals = useMemo(() => sampleTotals(sheets), [sheets]);
  const allDue = useMemo(() => sheets.flatMap((item) => item.rows
    .filter((row) => row.sampleReminderDate && !row.sampleReminderDone && row.sampleReminderDate <= asOf)
    .map((row) => ({ sheet: item, row }))), [asOf, sheets]);
  const dueReminders = useMemo(() => allDue.filter(({ row }) => row.sampleReminderUserId === currentUserId), [allDue, currentUserId]);
  const otherReminders = useMemo(() => allDue.filter(({ row }) => row.sampleReminderUserId !== currentUserId), [allDue, currentUserId]);
  const names = useMemo(() => Object.fromEntries(people.map((person) => [person.id, person.name])), [people]);

  useEffect(() => {
    if (sheets.length) onDueReminders?.(dueReminders.length);
  }, [dueReminders.length, onDueReminders, sheets.length]);

  const hasSheet = Boolean(sheet);
  const columnsKey = sheet ? sheet.columns.join('|') : '';
  useEffect(() => {
    if (hasSheet) setHidden(hiddenColumns(sheetId, columnsKey ? columnsKey.split('|') : []));
  }, [columnsKey, hasSheet, sheetId]);

  useEffect(() => { setSelected([]); }, [sheetId]);

  const weeks = useMemo(() => {
    if (!sheet) return [];
    const rows = sheet.rows.filter((row) => {
      const productName = athenaProductName(products[row.id]) || row.articleName;
      if (filter === 'products' && !productName.trim() && !row.articleNumber.trim()) return false;
      if (!needle) return true;
      return [row.category, productName, row.articleNumber].join(' ').toLocaleLowerCase('sv').includes(needle);
    });
    return groupRows(rows);
  }, [filter, needle, products, sheet]);

  const run = async (action: () => Promise<void>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (requestError: any) {
      if (requestError?.response?.status === 409) {
        setNotice('Någon annan hann ändra årskalendern. Den har laddats om.');
        load();
      } else {
        setError(apiErrorMessage(requestError, fallback));
      }
    } finally {
      setBusy(false);
    }
  };

  const replaceSheet = (next: YearbookSheet, refreshWindows: boolean) => {
    setSheets((current) => current.map((item) => (item.id === next.id ? next : item)));
    if (refreshWindows) launchApi.calendarWindows().then(setWindows).catch(() => undefined);
  };

  const handleCommandError = (requestError: any, fallback: string) => {
    if (requestError?.response?.status === 409) {
      setNotice('Någon annan hann ändra årskalendern. Den har laddats om.');
      load();
    } else {
      setError(apiErrorMessage(requestError, fallback));
    }
  };

  useEffect(() => {
    if (!canEdit || !sheet || productsFailed) return undefined;
    const pending = sheet.rows.some((row) => {
      const title = athenaProductName(products[row.id]);
      const ean = athenaEan(products[row.id]);
      return Boolean((title && title !== row.articleName.trim()) || (ean && ean !== (row.ean || '')));
    });
    if (!pending) return undefined;
    const key = `${sheet.id}:${sheet.version}`;
    if (syncedNames.current === key) return undefined;
    syncedNames.current = key;
    let cancelled = false;
    launchApi.syncYearbookProductNames(sheet.id, commandId())
      .then((next) => { if (!cancelled && next.version !== sheet.version) replaceSheet(next, touchesWindow(sheet.rows, ['articleName'])); })
      .catch((requestError: any) => {
        if (cancelled || requestError?.response?.status !== 409) return;
        setNotice('Någon annan hann ändra årskalendern. Den har laddats om.');
        load();
      });
    return () => { cancelled = true; };
  }, [canEdit, products, productsFailed, sheet]);

  const saveRow = async (row: YearbookRow, patch: Record<string, unknown>): Promise<boolean> => {
    if (!sheet) return false;
    setError(null);
    try {
      const next = await launchApi.yearbookCommand(sheet.id, {
        type: 'updateRow', commandId: commandId(), rowId: row.id, expectedRowVersion: row.version, expectedSheetVersion: sheet.version, patch,
      });
      replaceSheet(next, touchesWindow([row], Object.keys(patch)));
      return true;
    } catch (requestError) {
      handleCommandError(requestError, 'Raden kunde inte sparas.');
      return false;
    }
  };

  const selectedRows = useMemo(
    () => (sheet ? sheet.rows.filter((row) => selected.includes(row.id)) : []),
    [selected, sheet],
  );
  const selectedSet = useMemo(() => new Set(selectedRows.map((row) => row.id)), [selectedRows]);

  const select = (rowIds: string[], on: boolean) => {
    setSelected((current) => (on ? Array.from(new Set([...current, ...rowIds])) : current.filter((id) => !rowIds.includes(id))));
  };

  const copyEans = async () => {
    if (!sheet) return;
    const ordered = groupRows(sheet.rows).flatMap((week) => week.categories.flatMap((category) => category.rows)).filter((row) => selectedSet.has(row.id));
    const codes = ordered.map((row) => athenaEan(products[row.id]) || row.ean || '').filter(Boolean);
    const missing = ordered.length - codes.length;
    setError(null);
    if (codes.length === 0) {
      setError('Ingen av de markerade raderna har en EAN-kod.');
      return;
    }
    try {
      await navigator.clipboard.writeText(codes.join('\n'));
      const copied = codes.length === 1 ? '1 EAN-kod är kopierad' : `${codes.length} EAN-koder är kopierade`;
      setNotice(missing ? `${copied}. ${missing === 1 ? '1 rad saknar' : `${missing} rader saknar`} EAN.` : `${copied}.`);
    } catch {
      setError('EAN-koderna kunde inte kopieras till urklipp.');
    }
  };

  const addRow = (patch: Record<string, unknown>, noticeText: string, fallback: string) => {
    if (!sheet) return;
    setFilter('all');
    void run(async () => {
      const next = await launchApi.yearbookCommand(sheet.id, {
        type: 'addRow',
        commandId: commandId(),
        expectedSheetVersion: sheet.version,
        patch,
      });
      replaceSheet(next, false);
      setNotice(noticeText);
    }, fallback);
  };

  const addWeek = () => {
    if (!sheet) return;
    const used = new Set(sheet.rows.map((row) => row.announcementRaw.trim().toLocaleLowerCase('sv')));
    addRow({
      announcementRaw: uniqueLabel(used, 'ny vecka').replace(/^ny vecka/, 'Ny vecka'),
      category: 'Ny kategori',
    }, 'Veckan är tillagd. Sätt datum under Redigera vecka.', 'Veckan kunde inte läggas till.');
  };

  const addCategory = (week: YearbookWeekGroup) => {
    const used = new Set(week.categories.map((category) => category.category.trim().toLocaleLowerCase('sv')));
    addRow({
      ...weekFields(week),
      category: uniqueLabel(used, 'ny kategori').replace(/^ny kategori/, 'Ny kategori'),
    }, week.windowFamilyId ? 'Kategorin är tillagd. Publicera ändringen under Kedjekalender.' : 'Kategorin är tillagd.', 'Kategorin kunde inte läggas till.');
  };

  const addProduct = (week: YearbookWeekGroup, category: string) => {
    addRow({
      ...weekFields(week),
      category,
    }, 'Raden är tillagd.', 'Raden kunde inte läggas till.');
  };

  const renameCategory = async (rows: YearbookRow[], name: string): Promise<boolean> => {
    const next = name.trim();
    if (!sheet || rows.length === 0 || !next || next.length > 80) return false;
    if (rows.every((row) => row.category === next)) return true;
    setError(null);
    try {
      const updated = await launchApi.yearbookCommand(sheet.id, {
        type: 'updateRows',
        commandId: commandId(),
        rowIds: rows.map((row) => row.id),
        expectedRowVersions: Object.fromEntries(rows.map((row) => [row.id, row.version])),
        expectedSheetVersion: sheet.version,
        patch: { category: next },
      });
      replaceSheet(updated, touchesWindow(rows, ['category']));
      return true;
    } catch (requestError) {
      handleCommandError(requestError, 'Kategorin kunde inte byta namn.');
      return false;
    }
  };

  const applyBulk = async (field: BulkField, value: string) => {
    if (!sheet || selectedRows.length === 0) return;
    const targets = field === 'samples' ? selectedRows.filter((row) => !row.recipients?.length) : selectedRows;
    const skipped = selectedRows.length - targets.length;
    const skippedText = skipped === 0 ? '' : ` ${skipped === 1 ? '1 rad har' : `${skipped} rader har`} mottagare och ändrades inte. Ändra antalet där.`;
    if (targets.length === 0) {
      setNotice(null);
      setError(`Ingen rad ändrades.${skippedText}`);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const next = await launchApi.yearbookCommand(sheet.id, {
        type: 'updateRows',
        commandId: commandId(),
        rowIds: targets.map((row) => row.id),
        expectedRowVersions: Object.fromEntries(targets.map((row) => [row.id, row.version])),
        expectedSheetVersion: sheet.version,
        patch: { [field]: value },
      });
      replaceSheet(next, touchesWindow(targets, [field]));
      setNotice(`${targets.length} ${targets.length === 1 ? 'rad är uppdaterad' : 'rader är uppdaterade'}.${skippedText}`);
    } catch (requestError) {
      handleCommandError(requestError, 'Raderna kunde inte uppdateras.');
    } finally {
      setBusy(false);
    }
  };

  const removeRows = async () => {
    if (!sheet || pendingDelete.length === 0) return;
    const targets = pendingDelete;
    setBusy(true);
    setError(null);
    try {
      let current = sheet;
      for (const row of targets) {
        const live = current.rows.find((item) => item.id === row.id);
        if (!live) continue;
        current = await launchApi.yearbookCommand(sheet.id, {
          type: 'deleteRow',
          commandId: commandId(),
          rowId: live.id,
          expectedRowVersion: live.version,
          expectedSheetVersion: current.version,
        });
      }
      replaceSheet(current, targets.some((row) => row.windowFamilyId));
      setSelected((currentIds) => currentIds.filter((id) => !targets.some((row) => row.id === id)));
      setPendingDelete([]);
      setNotice(targets.length === 1 ? 'Raden är borttagen.' : `${targets.length} rader är borttagna.`);
    } catch (requestError) {
      handleCommandError(requestError, 'Raden kunde inte tas bort.');
    } finally {
      setBusy(false);
    }
  };

  const toggleColumn = (column: RowColumn) => {
    if (!sheet) return;
    const next = hidden.includes(column) ? hidden.filter((item) => item !== column) : [...hidden, column];
    setHidden(next);
    saveHiddenColumns(sheet.id, next);
  };

  const visible = useMemo(() => ROW_COLUMNS.filter((column) => !hidden.includes(column)), [hidden]);
  const sheetChain = customerChainOf(sheet?.chain || '');
  const chainContacts = useMemo(() => contacts.filter((contact) => contact.chain === sheetChain), [contacts, sheetChain]);

  return (
    <Stack spacing={2}>
      {error && <Alert severity="error" onClose={() => setError(null)}>{error}</Alert>}
      {notice && <Alert severity="success" onClose={() => setNotice(null)}>{notice}</Alert>}
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography component="h2" sx={{ fontSize: 22, fontWeight: 700, color: launchColors.navy }}>Årskalender</Typography>
        <InfoTip text={help.yearbook} />
        <Box sx={{ flex: 1 }} />
        <Button onClick={() => setCustomerOpen(true)}>Kundinformation</Button>
        <InfoTip text={help.yearbookContacts} />
      </Stack>
      {status === 'loading' ? (
        <Stack spacing={1} aria-busy="true" aria-label="Laddar årskalendern">
          <LinearProgress />
          <Typography sx={{ color: launchColors.textSecondary }}>Laddar årskalendern…</Typography>
        </Stack>
      ) : status === 'failed' ? (
        <Box>
          <Button onClick={() => { setError(null); load(); }}>Försök igen</Button>
        </Box>
      ) : sheets.length === 0 ? (
        <SectionCard title="Inget underlag" headingLevel="h3">
          <Typography sx={{ color: launchColors.textSecondary, mb: 2 }}>Årskalendern är tom. Excel-bladen kan läsas in en gång.</Typography>
          {canImport && (
            <Button variant="contained" disabled={busy} onClick={() => { void run(async () => { await launchApi.importYearbook(); setNotice('Excel-underlaget är inläst.'); load(); }, 'Underlaget kunde inte läsas in.'); }}>
              Läs in Excel-underlaget
            </Button>
          )}
        </SectionCard>
      ) : (
        <>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Tabs value={sheetId} onChange={(_event, value: string) => setSheetId(value)} variant="scrollable" scrollButtons="auto" aria-label="Kedjor i årskalendern">
              {sheets.map((item) => <Tab key={item.id} value={item.id} label={item.name} />)}
            </Tabs>
            {canEdit && <Button startIcon={<AddIcon />} onClick={() => setNewChainOpen(true)}>Ny kedja</Button>}
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }}>
            <TextField size="small" label="Sök produkt, artikelnummer eller kategori" value={query} onChange={(event) => setQuery(event.target.value)} sx={{ flex: 1, bgcolor: launchColors.card }} />
            {(['all', 'products'] as Filter[]).map((item) => (
              <Button key={item} sx={chipButtonSx(filter === item)} onClick={() => setFilter(item)}>
                {item === 'all' ? 'Alla' : 'Dölj tomma rader'}
              </Button>
            ))}
            <InfoTip text={help.yearbookIssues} />
            <Button startIcon={<ViewColumnIcon />} aria-label="Kolumner" onClick={(event) => setColumnMenu(event.currentTarget)}>Kolumner</Button>
            <InfoTip text={help.yearbookColumns} />
            {canEdit && <Button variant="contained" startIcon={<AddIcon />} onClick={addWeek}>Lägg till ny vecka</Button>}
          </Stack>
          <Menu anchorEl={columnMenu} open={Boolean(columnMenu)} onClose={() => setColumnMenu(null)}>
            {ROW_COLUMNS.map((column) => (
              <MenuItem key={column} onClick={() => toggleColumn(column)}>
                <Checkbox size="small" checked={!hidden.includes(column)} />
                {COLUMN_LABELS[column]}
              </MenuItem>
            ))}
          </Menu>
          {productsFailed && <Alert severity="warning">Kopplingen till produkter i Athena kunde inte hämtas. Artikelnumren visas som text.</Alert>}
          <ReminderAlerts
            own={dueReminders}
            others={otherReminders}
            userId={currentUserId}
            sheetId={sheetId}
            asOf={asOf}
            names={names}
            onShow={setSheetId}
          />
          {weeks.length === 0 ? (
            <Typography sx={{ color: launchColors.textSecondary }}>Inga rader matchar.</Typography>
          ) : (
            <YearbookTable
              weeks={weeks}
              visible={visible}
              windows={windows}
              canEdit={canEdit}
              canOpenProductInfo={canOpenProductInfo}
              canOpenAlcoholInfo={canOpenAlcoholInfo}
              products={products}
              selected={selectedSet}
              onSelect={select}
              onEditWeek={(week) => setWeekTarget(week)}
              onOpenRow={setRowTarget}
              onAddCategory={addCategory}
              onAddProduct={addProduct}
              onRenameCategory={(rows, name) => renameCategory(rows, name)}
              onSaveText={(row, column, value) => saveRow(row, { [column]: value })}
              onSaveReminder={(row, patch) => saveRow(row, patch)}
              sampleTotals={totals}
              asOf={asOf}
              onSaveArticle={(row, patch) => saveRow(row, { ...patch })}
              onSaveRecipients={(row, recipients) => saveRow(row, { recipients })}
              onOpenCards={(cards, start) => setOpenCards({ cards, start })}
              expanded={expanded}
              onToggleRecipients={(rowId) => setExpanded((current) => {
                const next = new Set(current);
                if (next.has(rowId)) next.delete(rowId);
                else next.add(rowId);
                return next;
              })}
              contacts={chainContacts}
              onDeleteRows={setPendingDelete}
            />
          )}
          {canEdit && selectedRows.length > 0 && (
            <BulkEditBar
              count={selectedRows.length}
              busy={busy}
              onClear={() => setSelected([])}
              onApply={(field, value) => { void applyBulk(field, value); }}
              onDelete={() => setPendingDelete(selectedRows)}
              onCopyEan={() => { void copyEans(); }}
            />
          )}
        </>
      )}
      {sheet && (
        <RowDialog
          open={Boolean(rowTarget)}
          row={rowTarget}
          productTitle={rowTarget ? athenaProductName(products[rowTarget.id]) : ''}
          productEan={rowTarget ? athenaEan(products[rowTarget.id]) : ''}
          canEdit={canEdit}
          busy={busy}
          onClose={() => setRowTarget(null)}
          onSave={(patch) => {
            const target = rowTarget;
            if (!target) return;
            void run(async () => {
              const next = await launchApi.yearbookCommand(sheet.id, {
                type: 'updateRow', commandId: commandId(), rowId: target.id, expectedRowVersion: target.version, expectedSheetVersion: sheet.version, patch,
              });
              replaceSheet(next, touchesWindow([target], Object.keys(patch)));
              setRowTarget(null);
              setNotice('Raden är sparad.');
            }, 'Raden kunde inte sparas.');
          }}
          onDelete={() => {
            const target = rowTarget;
            if (!target) return;
            void run(async () => {
              const next = await launchApi.yearbookCommand(sheet.id, {
                type: 'deleteRow', commandId: commandId(), rowId: target.id, expectedRowVersion: target.version, expectedSheetVersion: sheet.version,
              });
              replaceSheet(next, Boolean(target.windowFamilyId));
              setRowTarget(null);
              setNotice('Raden är borttagen.');
            }, 'Raden kunde inte tas bort.');
          }}
        />
      )}
      {sheet && weekTarget && (
        <WeekDialog
          week={weekTarget}
          busy={busy}
          published={Boolean(weekTarget.windowFamilyId)}
          onClose={() => setWeekTarget(null)}
          onSave={(values) => {
            const versions = Object.fromEntries(weekTarget.categories.flatMap((category) => category.rows.map((row) => [row.id, row.version])));
            void run(async () => {
              const next = await launchApi.yearbookCommand(sheet.id, {
                type: 'updateWeek',
                commandId: commandId(),
                weekKey: weekTarget.weekKey,
                expectedRowVersions: versions,
                expectedSheetVersion: sheet.version,
                ...values,
              });
              replaceSheet(next, Boolean(weekTarget.windowFamilyId));
              setWeekTarget(null);
              setNotice(weekTarget.windowFamilyId ? 'Veckan är ändrad. Publicera ändringen under Kedjekalender.' : 'Veckan är ändrad.');
            }, 'Veckan kunde inte ändras.');
          }}
        />
      )}
      <Dialog open={pendingDelete.length > 0} onClose={() => setPendingDelete([])} fullWidth maxWidth="xs">
        <DialogTitle>{pendingDelete.length === 1 ? 'Ta bort rad' : `Ta bort ${pendingDelete.length} rader`}</DialogTitle>
        <DialogContent>
          <Typography sx={{ color: launchColors.textSecondary }}>
            {pendingDelete.length === 1
              ? `${pendingDelete[0].articleName || pendingDelete[0].articleNumber || 'Raden'} tas bort från veckan.`
              : 'De markerade raderna tas bort från veckan.'}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPendingDelete([])}>Avbryt</Button>
          <Button color="error" variant="contained" disabled={busy} onClick={() => { void removeRows(); }}>Ta bort</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={newChainOpen} onClose={() => setNewChainOpen(false)} fullWidth maxWidth="xs">
        <DialogTitle>Ny kedja</DialogTitle>
        <DialogContent>
          <TextField autoFocus fullWidth margin="dense" label="Namn" value={newChainName} onChange={(event) => setNewChainName(event.target.value)} />
          <Typography sx={{ color: launchColors.textSecondary, mt: 1 }}>Lägg också till kedjan under Inställningar och Kedjor, om den ska gå att välja när en lansering skapas.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewChainOpen(false)}>Avbryt</Button>
          <Button variant="contained" disabled={busy || !newChainName.trim()} onClick={() => {
            void run(async () => {
              const created = await launchApi.addYearbookSheet(newChainName.trim());
              setSheets((current) => [...current, created]);
              setSheetId(created.id);
              setNewChainName('');
              setNewChainOpen(false);
              setNotice('Kedjan är tillagd.');
            }, 'Kedjan kunde inte läggas till.');
          }}>Lägg till</Button>
        </DialogActions>
      </Dialog>
      <CustomerInfoDialog
        open={customerOpen}
        contacts={contacts}
        canEdit={canEdit}
        defaultChain={customerChainOf(sheet?.chain || '')}
        onClose={() => setCustomerOpen(false)}
        onChanged={setContacts}
      />
      {openCards && <ContactCardsDialog cards={openCards.cards} start={openCards.start} onClose={() => setOpenCards(null)} />}
    </Stack>
  );
};

const RowDialog: React.FC<{
  open: boolean;
  row: YearbookRow | null;
  productTitle: string;
  productEan: string;
  canEdit: boolean;
  busy: boolean;
  onClose: () => void;
  onSave: (patch: Record<string, string | number | null>) => void;
  onDelete: () => void;
}> = ({ open, row, productTitle, productEan, canEdit, busy, onClose, onSave, onDelete }) => {
  const [draft, setDraft] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!open || !row) return;
    setDraft({
      announcementDate: row.announcementDate || '',
      statedWeek: row.statedWeek?.toString() || '',
      revisionWeek: row.revisionWeek?.toString() || '',
      revisionYear: row.revisionYear?.toString() || '',
      category: row.category,
      articleNumber: row.articleNumber || '',
      articleName: productTitle || row.articleName || '',
      ean: productEan || row.ean || '',
      action: row.action || '',
      info: row.info || '',
      commentKam: row.commentKam || '',
      samples: row.samples || '',
    });
  }, [open, productEan, productTitle, row]);
  const numberOrNull = (value: string) => value.trim() ? Number(value) : null;
  const hasRecipients = Boolean(row?.recipients?.length);
  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Rad</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          {row?.announcementRaw && <Typography sx={{ color: launchColors.textSecondary }}>Från Excel: {row.announcementRaw}</Typography>}
          <TextField type="date" label="Aviseringsdatum" value={draft.announcementDate || ''} onChange={(event) => setDraft({ ...draft, announcementDate: event.target.value })} InputLabelProps={{ shrink: true }} disabled={!canEdit} />
          <Stack direction="row" spacing={1}>
            <TextField label="Aviseringsvecka" value={draft.statedWeek || ''} onChange={(event) => setDraft({ ...draft, statedWeek: event.target.value })} disabled={!canEdit} />
            <TextField label="Revideringsvecka" value={draft.revisionWeek || ''} onChange={(event) => setDraft({ ...draft, revisionWeek: event.target.value })} disabled={!canEdit} />
            <TextField label="Revideringsår" value={draft.revisionYear || ''} onChange={(event) => setDraft({ ...draft, revisionYear: event.target.value })} disabled={!canEdit} />
          </Stack>
          <TextField label="Kategori" value={draft.category || ''} onChange={(event) => setDraft({ ...draft, category: event.target.value })} disabled={!canEdit} />
          <TextField label="Artikelnummer" value={draft.articleNumber || ''} onChange={(event) => setDraft({ ...draft, articleNumber: event.target.value })} disabled={!canEdit} />
          <TextField label="Produkt" value={draft.articleName || ''} onChange={(event) => setDraft({ ...draft, articleName: event.target.value })} disabled={!canEdit || Boolean(productTitle)} helperText={productTitle ? 'Namnet kommer från Athena.' : undefined} />
          <TextField label="EAN" value={draft.ean || ''} onChange={(event) => setDraft({ ...draft, ean: event.target.value })} disabled={!canEdit || Boolean(productEan)} helperText={productEan ? 'EAN-koden kommer från Athena.' : undefined} />
          <TextField label="Åtgärd" value={draft.action || ''} onChange={(event) => setDraft({ ...draft, action: event.target.value })} disabled={!canEdit} />
          <TextField label="Info" value={draft.info || ''} onChange={(event) => setDraft({ ...draft, info: event.target.value })} disabled={!canEdit} />
          <TextField label="Kommentar KAM" value={draft.commentKam || ''} onChange={(event) => setDraft({ ...draft, commentKam: event.target.value })} disabled={!canEdit} />
          <TextField label="Varuprover" value={draft.samples || ''} onChange={(event) => setDraft({ ...draft, samples: event.target.value })} disabled={!canEdit || hasRecipients} helperText={hasRecipients ? 'Summan räknas från mottagarna. Ändra antalet i kolumnen Kategoriansvarig.' : undefined} />
          {row && Object.keys(row.extra || {}).length > 0 && (
            <Typography sx={{ color: launchColors.textSecondary }}>Övrigt: {Object.entries(row.extra).map(([key, value]) => `${key}: ${value}`).join(' · ')}</Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        {canEdit && row && <Button color="inherit" disabled={busy} onClick={onDelete}>Ta bort</Button>}
        <Box sx={{ flex: 1 }} />
        <Button onClick={onClose}>Stäng</Button>
        {canEdit && (
          <Button variant="contained" disabled={busy} onClick={() => onSave({
            announcementDate: draft.announcementDate || null,
            statedWeek: numberOrNull(draft.statedWeek || ''),
            revisionWeek: numberOrNull(draft.revisionWeek || ''),
            revisionYear: numberOrNull(draft.revisionYear || ''),
            category: draft.category || '',
            articleNumber: draft.articleNumber || '',
            articleName: draft.articleName || '',
            ...(productEan ? {} : { ean: draft.ean || '' }),
            action: draft.action || '',
            info: draft.info || '',
            commentKam: draft.commentKam || '',
            ...(hasRecipients ? {} : { samples: draft.samples || '' }),
          })}>Spara</Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

const WeekDialog: React.FC<{
  week: YearbookWeekGroup;
  published: boolean;
  busy: boolean;
  onClose: () => void;
  onSave: (values: { announcementDate: string | null; statedWeek: number | null; revisionWeek: number | null; revisionYear: number | null }) => void;
}> = ({ week, published, busy, onClose, onSave }) => {
  const [date, setDate] = useState(week.announcementDate || '');
  const [stated, setStated] = useState(week.statedWeek?.toString() || '');
  const [revision, setRevision] = useState(week.revisionWeek?.toString() || '');
  const [year, setYear] = useState(week.revisionYear?.toString() || '');
  const count = week.categories.reduce((sum, category) => sum + category.rows.length, 0);
  const numberOrNull = (value: string) => value.trim() ? Number(value) : null;
  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Redigera vecka</DialogTitle>
      <DialogContent>
        <Stack spacing={1.5} sx={{ pt: 1 }}>
          <Typography sx={{ color: launchColors.textSecondary }}>{count} rader ändras.{published ? ' Veckan är publicerad.' : ''}</Typography>
          <TextField type="date" label="Aviseringsdatum" value={date} onChange={(event) => setDate(event.target.value)} InputLabelProps={{ shrink: true }} />
          <TextField label="Aviseringsvecka" value={stated} onChange={(event) => setStated(event.target.value)} />
          <TextField label="Revideringsvecka" value={revision} onChange={(event) => setRevision(event.target.value)} />
          <TextField label="Revideringsår" value={year} onChange={(event) => setYear(event.target.value)} />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Avbryt</Button>
        <Button variant="contained" disabled={busy} onClick={() => onSave({
          announcementDate: date || null,
          statedWeek: numberOrNull(stated),
          revisionWeek: numberOrNull(revision),
          revisionYear: numberOrNull(year),
        })}>Spara</Button>
      </DialogActions>
    </Dialog>
  );
};

export default YearbookView;
